import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useFeedStore } from '@/stores/feedStore';
import { BlahblahCard } from '@/components/BlahblahCard';
import type { FeedType } from '@/types';
import { cn } from '@/lib/utils';

export function FeedPage() {
  const {
    items,
    feedType,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadFeed,
    loadMore,
    setFeedType,
  } = useFeedStore();

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !isLoadingMore) {
      loadMore();
    }
  };

  const feedTypes: { type: FeedType; label: string }[] = [
    { type: 'all', label: 'For You' },
    { type: 'following', label: 'Following' },
  ];

  return (
    <div className="h-[calc(100vh-7.5rem)] overflow-y-auto" onScroll={handleScroll}>
      {/* Feed type tabs */}
      <div className="sticky top-0 z-10 bg-background border-b border-border">
        <div className="flex">
          {feedTypes.map(({ type, label }) => (
            <button
              key={type}
              onClick={() => setFeedType(type)}
              className={cn(
                'flex-1 py-3 text-sm font-medium transition-colors',
                feedType === type
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {label}
            </button>
          ))}
          <Link
            to="/categories"
            className="flex-1 py-3 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground text-center"
          >
            Categories
          </Link>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 text-center">
          <p className="text-red-500 mb-2">{error}</p>
          <button
            onClick={() => loadFeed()}
            className="text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && items.length === 0 && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && items.length === 0 && (
        <div className="text-center py-12 px-4">
          <p className="text-muted-foreground mb-2">No blahblahs yet</p>
          {feedType === 'following' && (
            <p className="text-sm text-muted-foreground/70">
              Follow some people to see their blahblahs here
            </p>
          )}
        </div>
      )}

      {/* Feed items */}
      <div>
        {items.map((blahblah) => (
          <BlahblahCard key={blahblah.id} blahblah={blahblah} />
        ))}
      </div>

      {/* Loading more indicator */}
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      )}

      {/* End of feed */}
      {!hasMore && items.length > 0 && (
        <p className="text-center text-muted-foreground text-sm py-4">
          You've reached the end
        </p>
      )}
    </div>
  );
}
